import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const METRICS = {
  projects:  { prefix: "",  number: "7",  suffix: "+", label: "Projects delivered" },
  clients:   { prefix: "",  number: "7",  suffix: "",  label: "Clients partnered"  },
  countries: { prefix: "",  number: "5",  suffix: "",  label: "Countries served"   },
  value:     { prefix: "$", number: "8",  suffix: "M+", label: "Value generated",
               basis: "Combined cost savings and revenue impact delivered across client engagements to date." },
};

// ── Rolling digit reel ────────────────────────────────────────────────────────

function Reel({ digit, size, play, pos }) {
  const ref  = useRef(null);
  const cellH = Math.round(size * 1.04);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const target = 10 + Number(digit);
    if (!play) {
      // Resting state IS the final digit — the prerendered HTML and the
      // pre-animation first paint both show the real number (audit T5).
      el.style.transition = "none";
      el.style.transform  = `translateY(-${target * cellH}px)`;
      return;
    }
    // Roll in from one full cycle above, landing on the same final digit.
    el.style.transition = "none";
    el.style.transform  = `translateY(-${Number(digit) * cellH}px)`;
    // Force a reflow so the transition below actually runs from that start point.
    void el.offsetHeight;
    const dur = 1.05 + pos * 0.28;
    el.style.transition = `transform ${dur}s cubic-bezier(0.16,1,0.3,1) ${pos * 0.06}s`;
    el.style.transform  = `translateY(-${target * cellH}px)`;
  }, [play, digit, cellH, pos]);

  const cells = [];
  for (let c = 0; c < 2; c++) for (let d = 0; d < 10; d++) cells.push(d);

  return (
    <span style={{ display: "inline-block", height: cellH, overflow: "hidden", verticalAlign: "top" }}>
      <span ref={ref} style={{ display: "block", willChange: "transform" }}>
        {cells.map((d, i) => (
          <span key={i} style={{
            display: "block", height: cellH, lineHeight: `${cellH}px`,
            textAlign: "center", fontVariantNumeric: "tabular-nums",
          }}>{d}</span>
        ))}
      </span>
    </span>
  );
}

// ── Odometer — rolling reels or static snap for reduced-motion ───────────────

function Odometer({ data, size, play, snap }) {
  const baseStyle = {
    fontFamily: "Montserrat, sans-serif", fontWeight: 300,
    fontSize: size, letterSpacing: "-0.035em", color: "#fff",
    display: "inline-flex", alignItems: "flex-start",
  };
  const accentStyle = { color: "#7BB5B1", fontWeight: 300 };

  if (snap) {
    return (
      <span style={baseStyle}>
        {data.prefix  && <span style={accentStyle}>{data.prefix}</span>}
        <span>{data.number}</span>
        {data.suffix  && <span style={accentStyle}>{data.suffix}</span>}
      </span>
    );
  }

  return (
    <span style={{ ...baseStyle, lineHeight: `${Math.round(size * 1.04)}px` }}>
      {data.prefix && <span style={accentStyle}>{data.prefix}</span>}
      {data.number.split("").map((d, i) => (
        <Reel key={i} digit={d} size={size} play={play} pos={i} />
      ))}
      {data.suffix && <span style={accentStyle}>{data.suffix}</span>}
    </span>
  );
}

// ── Glowing orbit dot + number + label ───────────────────────────────────────

function MetricNode({ data, size, play, snap, align }) {
  const right = align === "right";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: right ? "flex-end" : "flex-start", gap: 7 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 11, flexDirection: right ? "row-reverse" : "row" }}>
        <span className="abt-orb-dot" style={{
          display: "inline-block", width: 9, height: 9, borderRadius: "50%",
          background: "#d7efec",
          boxShadow: "0 0 10px 2px rgba(123,181,177,0.75), 0 0 22px 6px rgba(91,148,144,0.35)",
          animation: "abt-orbPulse 3.2s cubic-bezier(0.4,0,0.2,1) infinite",
          marginTop: size * 0.34, flexShrink: 0,
        }} />
        <Odometer data={data} size={size} play={play} snap={snap} />
      </div>
      <span style={{
        fontFamily: "Montserrat, sans-serif", fontSize: 12, fontWeight: 600,
        letterSpacing: "0.13em", textTransform: "uppercase", color: "#9CA3AF",
        paddingLeft: right ? 0 : 20, paddingRight: right ? 20 : 0,
        textAlign: right ? "right" : "left",
      }}>{data.label}</span>
    </div>
  );
}

// ── Pyramid — 3D intense floating + light pool + glows ────────────────────────
// grassurn-icon.png has a white background; the hidden SVG filter strips it.

function Pyramid({ size = 86 }) {
  return (
    <div style={{ position: "relative", display: "grid", placeItems: "center", perspective: "520px" }}>
      {/* Grounding light pool — foreshortened onto the orbital plane */}
      <div className="abt-pyr-pool" style={{
        position: "absolute", top: "61%",
        width: size * 2.85, height: size * 1.02,
        transform: "translateY(-50%) rotate(-16deg)",
        background: "radial-gradient(ellipse at center, rgba(91,148,144,0.34), rgba(91,148,144,0.10) 46%, transparent 72%)",
        filter: "blur(7px)",
        animation: "abt-pyrPool 4.6s cubic-bezier(0.4,0,0.2,1) infinite",
      }} />
      {/* Soft ambient aura */}
      <div style={{
        position: "absolute", width: size * 2.3, height: size * 2.3, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(91,148,144,0.22), transparent 62%)",
        pointerEvents: "none",
      }} />
      {/* Bright core bloom */}
      <div style={{
        position: "absolute", width: size * 1.35, height: size * 1.35, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(123,181,177,0.32), transparent 60%)",
        filter: "blur(4px)", pointerEvents: "none",
      }} />
      {/* 3D floating pyramid image — white bg stripped via SVG filter */}
      <div className="abt-pyr-float" style={{
        animation: "abt-pyrFloat3d 10s cubic-bezier(0.4,0,0.2,1) infinite",
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}>
        <svg
          width={size} height={size}
          viewBox="0 0 1024 1024"
          preserveAspectRatio="xMidYMid meet"
          style={{
            display: "block", overflow: "visible",
            filter: "drop-shadow(0 18px 22px rgba(0,0,0,0.6)) drop-shadow(0 0 22px rgba(91,148,144,0.55))",
          }}
        >
          <image
            href="/grassurn-icon.png"
            x="0" y="0" width="1024" height="1024"
            filter="url(#abt-strip-white)"
          />
        </svg>
      </div>
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────

const AboutSection = () => {
  const [play, setPlay] = useState(false);
  const sectionRef = useRef(null);
  const copyRef    = useRef(null);

  const prefersReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  // IntersectionObserver — fire odometer when ≥30% of section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (prefersReducedMotion) { setPlay(true); return; }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setPlay(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [prefersReducedMotion]);

  // Scroll-reveal — stagger h2 → p when copy column enters viewport.
  // Uses IntersectionObserver instead of GSAP ScrollTrigger so it works
  // reliably on mobile (iOS Safari dynamic viewport breaks ScrollTrigger).
  useEffect(() => {
    if (prefersReducedMotion || !copyRef.current) return;
    const el = copyRef.current;
    const children = Array.from(el.children);
    gsap.set(children, { opacity: 0, y: 24 });
    let tween;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tween = gsap.to(children, {
            opacity: 1, y: 0,
            stagger: 0.12, duration: 0.75, ease: "power3.out",
          });
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (tween) tween.kill();
      gsap.set(children, { clearProps: "opacity,y" });
    };
  }, [prefersReducedMotion]);

  return (
    <section
      ref={sectionRef}
      id="about"
      className="about-section-el"
      style={{
        position: "relative",
        background:
          "linear-gradient(to bottom, #070808 0%, rgba(7,8,8,0) 160px), radial-gradient(ellipse 90% 120% at 60% 40%, #0e1313 0%, #060707 70%)",
        overflow: "hidden",
        minHeight: 640,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "96px 64px",
      }}
    >
      {/* ── CSS animation keyframes ───────────────────────────────────────── */}
      <style>{`
        @keyframes abt-orbPulse {
          0%,100% { opacity: .65; transform: scale(0.92); }
          50%      { opacity: 1;   transform: scale(1.18); }
        }
        @keyframes abt-pyrFloat3d {
          0%,100% { transform: rotateX(21deg) rotateY(-14deg) translateY(4px); }
          50%      { transform: rotateX(21deg) rotateY(14deg)  translateY(-10px); }
        }
        @keyframes abt-pyrPool {
          0%,100% { opacity: .72; }
          50%      { opacity: 1;  }
        }
        @keyframes abt-driftA {
          0%,100% { transform: translate(0, 0);     opacity: .5; }
          50%      { transform: translate(-22px, 14px); opacity: 1;  }
        }
        @keyframes abt-driftB {
          0%,100% { transform: translate(0, 0);    opacity: .4; }
          50%      { transform: translate(18px,-16px); opacity: .9; }
        }
        @media (prefers-reduced-motion: reduce) {
          .abt-orb-dot, .abt-sat, .abt-pyr-float, .abt-pyr-pool {
            animation: none !important;
          }
        }
      `}</style>

      {/* Hidden SVG filter — strips white background from grassurn-icon.png.
          feColorMatrix: A' = -R -G -B + 3·A, so white (1,1,1,1)→A'=0 (transparent);
          teal pixels survive. Defined once here, referenced from the Pyramid SVG. */}
      <svg style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
        <defs>
          <filter id="abt-strip-white" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  -1 -1 -1 3 0" />
          </filter>
        </defs>
      </svg>

      <div className="about-section-inner" style={{
        display: "flex", alignItems: "center", gap: 28,
        maxWidth: 1280, margin: "0 auto", width: "100%",
      }}>

        {/* ── Left: approach copy ──────────────────────────────────────────── */}
        <div ref={copyRef} className="about-copy-col" style={{ width: "44%", flexShrink: 0 }}>

          {/* Headline */}
          <h2 style={{
            fontFamily: "Montserrat, sans-serif", fontWeight: 300,
            fontSize: "clamp(34px, 3.4vw, 52px)", lineHeight: 1.08,
            letterSpacing: "-0.025em", color: "#fff", margin: 0,
          }}>
            Building tomorrow's technology,{" "}
            <span style={{ color: "#7BB5B1", fontStyle: "italic" }}>one breakthrough at a time.</span>
          </h2>

          {/* Body */}
          <p style={{
            fontFamily: "system-ui, -apple-system, sans-serif", fontSize: 16, fontWeight: 300,
            lineHeight: 1.7, color: "rgba(255,255,255,0.55)", margin: "26px 0 32px", maxWidth: 430,
          }}>
            Grassurn Tech is an AI and innovation studio based in Toronto. We partner with
            ambitious teams to design, build, and ship technology that makes a real
            difference. From early-stage startups to enterprise teams that are ready to modernise.
          </p>

        </div>

        {/* ── Mobile stats grid — hidden on desktop, shown on mobile ──────── */}
        <div className="about-stats-mobile" style={{
          display: "none",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          width: "100%",
        }}>
          {Object.values(METRICS).map((m) => (
            <div key={m.label} style={{
              background: "linear-gradient(145deg, #141f1e 0%, #0c1717 100%)",
              border: "1px solid rgba(91,148,144,0.35)",
              borderRadius: 12,
              padding: "20px 16px",
              textAlign: "center",
              boxShadow: "0 0 22px rgba(91,148,144,0.14), 0 4px 18px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}>
              <div style={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 300,
                fontSize: "clamp(28px, 7vw, 40px)",
                letterSpacing: "-0.03em",
                color: "#b8e5e2",
                textShadow: "0 0 18px rgba(91,148,144,0.65), 0 0 40px rgba(91,148,144,0.30)",
              }}>
                <span style={{ color: "#7BB5B1" }}>{m.prefix}</span>
                {m.number}
                <span style={{ color: "#7BB5B1" }}>{m.suffix}</span>
              </div>
              <div style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#c8dedd",
                marginTop: 8,
              }}>
                {m.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── Right: orbital field ─────────────────────────────────────────── */}
        <div className="about-orbital-col" style={{ flex: 1, height: 560, position: "relative" }}>

          {/* Three concentric dotted ellipses, tilted -16° */}
          <svg
            viewBox="0 0 600 600"
            preserveAspectRatio="xMidYMid meet"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
          >
            {[
              { rx: 270, ry: 132, op: 0.55 },
              { rx: 200, ry: 96,  op: 0.70 },
              { rx: 120, ry: 58,  op: 0.85 },
            ].map((r, i) => (
              <ellipse key={i} cx="300" cy="300" rx={r.rx} ry={r.ry}
                transform="rotate(-16 300 300)"
                fill="none" stroke="rgba(123,181,177,0.34)" strokeWidth="1.4"
                strokeDasharray="2 9" strokeLinecap="round"
                style={{ opacity: r.op }}
              />
            ))}
          </svg>

          {/* Field radial glow */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "radial-gradient(circle at 54% 50%, rgba(45,92,90,0.20), transparent 55%)",
          }} />

          {/* Drifting satellite decorations */}
          <span className="abt-sat" style={{
            position: "absolute", left: "70%", top: "26%",
            width: 6, height: 6, borderRadius: "50%", background: "#bfe2de",
            boxShadow: "0 0 10px 3px rgba(123,181,177,0.6)",
            animation: "abt-driftA 11s cubic-bezier(0.4,0,0.2,1) infinite",
          }} />
          <span className="abt-sat" style={{
            position: "absolute", left: "38%", top: "64%",
            width: 6, height: 6, borderRadius: "50%", background: "#bfe2de",
            boxShadow: "0 0 10px 3px rgba(123,181,177,0.6)",
            animation: "abt-driftB 14s cubic-bezier(0.4,0,0.2,1) infinite",
          }} />

          {/* Central pyramid */}
          <div style={{ position: "absolute", left: "54%", top: "50%", transform: "translate(-50%,-50%)" }}>
            <Pyramid size={86} />
          </div>

          {/* Metric nodes — positioned on the orbital arcs */}
          <div style={{ position: "absolute", left: "50%", top: "9%", transform: "translate(-50%,-50%)" }}>
            <MetricNode data={METRICS.projects} size={50} play={play} snap={prefersReducedMotion} align="left" />
          </div>
          <div style={{ position: "absolute", left: "80%", top: "88%", transform: "translate(-100%,-50%)" }}>
            <MetricNode data={METRICS.clients} size={50} play={play} snap={prefersReducedMotion} align="right" />
          </div>
          <div style={{ position: "absolute", left: "11%", top: "76%", transform: "translate(0,-50%)" }}>
            <MetricNode data={METRICS.countries} size={50} play={play} snap={prefersReducedMotion} align="left" />
          </div>
          <div style={{ position: "absolute", left: "89%", top: "30%", transform: "translate(-100%,-50%)" }}>
            <MetricNode data={METRICS.value} size={50} play={play} snap={prefersReducedMotion} align="right" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
